import React from 'react';
import {
  CheckCircle, ChevronLeft, Download, Eye,
  CreditCard as Edit3, Home, Building2, AlertTriangle,
  Info, ChevronDown, ChevronUp, X, MessageSquare, FileText,
  GitCompareArrows, FileSearch, Clipboard, Sparkles, ExternalLink, Scale,
} from 'lucide-react';
import type { ServiceType } from '../services/serviceOrderService';
import type { Annotation } from '../services/tenancyReviewRewrite';
import { documentConverter } from '../services/documentConverter';
import { AgreementDisplay } from './AgreementDisplay';
import { DiffView } from './DiffView';
import { buildHighlightedSegments, getMatchedAnnotationIds } from '../utils/annotationHighlighter';

interface CompletedDocumentPageProps {
  serviceType: ServiceType;
  rewrittenDocument: string;
  annotations: Annotation[];
  analysis: Record<string, unknown>;
  originalText: string;
  negotiationSummary?: string;
  orderId: string;
  onBack: () => void;
  hideBackButton?: boolean;
}

const SEVERITY_CONFIG: Record<string, { color: string; bg: string; label: string; highlightBg: string; highlightBorder: string; dot: string }> = {
  critical: { color: 'text-red-700', bg: 'bg-red-50', label: 'Critical', highlightBg: 'bg-red-100/60', highlightBorder: 'border-l-red-500', dot: 'bg-red-500' },
  high: { color: 'text-gray-900', bg: 'bg-gray-100', label: 'High', highlightBg: 'bg-gray-200/60', highlightBorder: 'border-l-gray-700', dot: 'bg-gray-700' },
  medium: { color: 'text-gray-700', bg: 'bg-gray-50', label: 'Medium', highlightBg: 'bg-gray-100/60', highlightBorder: 'border-l-gray-500', dot: 'bg-gray-500' },
  low: { color: 'text-gray-500', bg: 'bg-gray-50', label: 'Low', highlightBg: 'bg-gray-50/60', highlightBorder: 'border-l-gray-300', dot: 'bg-gray-300' },
};

type ViewMode = 'diff' | 'clean' | 'edit';

export function CompletedDocumentPage({
  serviceType,
  rewrittenDocument,
  annotations,
  analysis,
  originalText,
  negotiationSummary,
  orderId,
  onBack,
  hideBackButton,
}: CompletedDocumentPageProps) {
  const isCommercial = serviceType === 'commercial_lease_review';
  const [viewMode, setViewMode] = React.useState<ViewMode>(isCommercial ? 'diff' : 'clean');
  const [editedContent, setEditedContent] = React.useState(rewrittenDocument);
  const [showAnnotations, setShowAnnotations] = React.useState(false);
  const [severityFilter, setSeverityFilter] = React.useState<string>('all');
  const [expandedAnnotation, setExpandedAnnotation] = React.useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = React.useState(false);
  const [downloadOpen, setDownloadOpen] = React.useState(false);
  const [activeHighlight, setActiveHighlight] = React.useState<string | null>(null);
  const [mobileTab, setMobileTab] = React.useState<'diff' | 'original' | 'rewritten' | 'annotations'>(isCommercial ? 'diff' : 'rewritten');
  const [showNegotiationSummary, setShowNegotiationSummary] = React.useState(false);
  const [showConfetti, setShowConfetti] = React.useState(true);

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const downloadRef = React.useRef<HTMLDivElement>(null);
  const highlightRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const hunkRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const annotationCardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const isTenancy = serviceType === 'tenancy_review_rewrite';
  const gradient = 'from-gray-900 to-gray-800';
  const icon = isTenancy
    ? <Home className="w-5 h-5 text-white" />
    : <Building2 className="w-5 h-5 text-white" />;
  const title = isTenancy ? 'Tenancy Agreement Review & Re-Write' : 'Commercial Lease Review';

  const severityCounts = React.useMemo(() => {
    const counts: Record<string, number> = { all: annotations.length, critical: 0, high: 0, medium: 0, low: 0 };
    annotations.forEach((a) => { counts[a.severity] = (counts[a.severity] || 0) + 1; });
    return counts;
  }, [annotations]);

  const filteredAnnotations = severityFilter === 'all'
    ? annotations
    : annotations.filter((a) => a.severity === severityFilter);

  const matchedIds = React.useMemo(
    () => getMatchedAnnotationIds(rewrittenDocument, annotations),
    [rewrittenDocument, annotations],
  );

  const segments = React.useMemo(
    () => buildHighlightedSegments(rewrittenDocument, annotations),
    [rewrittenDocument, annotations],
  );

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  React.useEffect(() => {
    if (viewMode === 'edit' && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, [viewMode, editedContent]);

  React.useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const getActiveContent = () => viewMode === 'edit' ? editedContent : rewrittenDocument;

  const handleDownload = async (format: 'pdf' | 'docx' | 'txt') => {
    setDownloadOpen(false);
    const content = getActiveContent();
    const docTitle = `${title} - ${orderId.slice(0, 8)}`;
    try {
      if (format === 'txt') {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${docTitle}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        await documentConverter.downloadDocument({
          documentContent: content,
          documentTitle: docTitle,
          documentType: serviceType,
          format,
        });
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const scrollToHighlight = (annotationId: string) => {
    if (viewMode === 'diff') {
      const el = hunkRefs.current[annotationId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setActiveHighlight(annotationId);
        setTimeout(() => setActiveHighlight(null), 2500);
      }
    } else {
      const el = highlightRefs.current[annotationId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setActiveHighlight(annotationId);
        setTimeout(() => setActiveHighlight(null), 2500);
      }
    }
  };

  const scrollToAnnotation = (annotationId: string) => {
    setExpandedAnnotation(annotationId);
    const card = annotationCardRefs.current[annotationId];
    if (card) {
      setTimeout(() => card.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    }
    setActiveHighlight(annotationId);
    setTimeout(() => setActiveHighlight(null), 2500);
  };

  const handleHunkClick = (annotationId: string) => {
    scrollToAnnotation(annotationId);
  };

  const handleHighlightClick = (annotationId: string) => {
    scrollToAnnotation(annotationId);
  };

  const handleAnnotationClick = (annotationId: string) => {
    const isExpanded = expandedAnnotation === annotationId;
    setExpandedAnnotation(isExpanded ? null : annotationId);
    if (!isExpanded) {
      scrollToHighlight(annotationId);
    }
  };

  const renderHighlightedDocument = () => {
    return segments.map((seg, i) => {
      if (!seg.annotationId) {
        return <span key={i}>{seg.text}</span>;
      }
      const config = SEVERITY_CONFIG[seg.severity || 'low'];
      const isActive = activeHighlight === seg.annotationId;
      return (
        <span
          key={i}
          ref={(el) => { highlightRefs.current[seg.annotationId!] = el; }}
          onClick={() => handleHighlightClick(seg.annotationId!)}
          className={`
            cursor-pointer border-l-2 px-0.5 rounded-sm transition-all duration-300
            ${config.highlightBg} ${config.highlightBorder}
            ${isActive ? 'ring-2 ring-offset-1 ring-gray-400 shadow-sm' : ''}
            hover:brightness-95
          `}
        >
          {seg.text}
        </span>
      );
    });
  };

  const hasAnnotations = annotations.length > 0;
  const showAnnotationsPanel = showAnnotations && hasAnnotations;
  const linkedCount = matchedIds.size;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradient} flex-shrink-0 relative`}>
        <div className="absolute inset-0 bg-grid-pattern opacity-10 overflow-hidden" />
        <div className="relative px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="max-w-[1800px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!hideBackButton && (
                <button
                  onClick={onBack}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
              )}
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                {icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-bold text-white">{title}</h1>
                  {showConfetti && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[10px] font-bold text-white animate-scale-in">
                      <Sparkles className="w-3 h-3" />
                      Complete
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3 h-3 text-white/80" />
                  <span className="text-white/80 text-xs">{isCommercial ? 'Comparative Analysis Complete' : 'Review Complete'}</span>
                  <span className="text-white/40 text-xs mx-1">|</span>
                  <span className="text-white/60 text-xs font-mono">{orderId.slice(0, 8)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative" ref={downloadRef}>
                <button
                  onClick={() => setDownloadOpen(!downloadOpen)}
                  className="px-4 py-2 bg-white text-gray-900 rounded-xl text-xs font-semibold flex items-center gap-2 hover:bg-gray-100 transition-colors shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                  <ChevronDown className={`w-3 h-3 transition-transform ${downloadOpen ? 'rotate-180' : ''}`} />
                </button>
                {downloadOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-elevated z-50 py-1 animate-slide-down">
                    {[
                      { format: 'pdf' as const, label: 'PDF Document', icon: FileText },
                      { format: 'docx' as const, label: 'Word (DOCX)', icon: FileText },
                      { format: 'txt' as const, label: 'Plain Text', icon: FileText },
                    ].map((item) => (
                      <button
                        key={item.format}
                        onClick={() => handleDownload(item.format)}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
                      >
                        <item.icon className="w-4 h-4 text-gray-400" />
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
        <div className="h-full min-h-0 max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col overflow-hidden">
          <AnalysisSummaryBar
            showAnalysis={showAnalysis}
            setShowAnalysis={setShowAnalysis}
            analysis={analysis}
            annotations={annotations}
            isTenancy={isTenancy}
            isCommercial={isCommercial}
            gradient={gradient}
            negotiationSummary={negotiationSummary}
            showNegotiationSummary={showNegotiationSummary}
            setShowNegotiationSummary={setShowNegotiationSummary}
          />

          <ProcessStepsBar gradient={gradient} isCommercial={isCommercial} />

          {/* Mobile tabs */}
          <div className="lg:hidden mb-3 flex bg-white rounded-xl p-1 border border-gray-200 flex-shrink-0 shadow-sm">
            {(['rewritten', 'diff', 'original', 'annotations'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setMobileTab(tab)}
                className={`flex-1 py-2.5 px-2 rounded-lg text-xs font-medium transition-all ${
                  mobileTab === tab ? `bg-gradient-to-r ${gradient} text-white shadow-sm` : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {isCommercial
                  ? (tab === 'rewritten' ? 'Doc B' : tab === 'diff' ? 'Comparison' : tab === 'original' ? 'Doc A' : `Changes (${annotations.length})`)
                  : (tab === 'rewritten' ? 'Clean' : tab === 'diff' ? 'Changes' : tab === 'original' ? 'Original' : `Notes (${annotations.length})`)
                }
              </button>
            ))}
          </div>

          {/* Desktop layout */}
          <div className={`hidden lg:grid gap-0 flex-1 min-h-0 overflow-hidden ${showAnnotationsPanel ? 'grid-cols-[1fr_320px]' : 'grid-cols-1'}`}>
            <div className={`bg-white border border-gray-200 flex flex-col min-h-0 min-w-0 overflow-hidden shadow-sm ${showAnnotationsPanel ? 'rounded-l-xl border-r-0' : 'rounded-xl'}`}>
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-white flex-shrink-0">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {viewMode === 'diff'
                      ? 'Comparison View'
                      : viewMode === 'clean'
                        ? (isCommercial ? 'Revised Lease' : 'Rewritten Document')
                        : 'Editing Document'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode('clean')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                        viewMode === 'clean' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Eye className="w-3 h-3" />
                      {isCommercial ? 'Doc B' : 'Clean'}
                    </button>
                    <button
                      onClick={() => setViewMode('diff')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                        viewMode === 'diff' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <GitCompareArrows className="w-3 h-3" />
                      Diff
                    </button>
                    {!isCommercial && (
                      <button
                        onClick={() => setViewMode('edit')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                          viewMode === 'edit' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>
                  {hasAnnotations && (
                    <button
                      onClick={() => setShowAnnotations(!showAnnotations)}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      {showAnnotations ? 'Hide' : 'Show'}
                    </button>
                  )}
                </div>
              </div>

              {viewMode === 'diff' ? (
                <DiffView
                  originalText={originalText}
                  rewrittenText={rewrittenDocument}
                  annotations={annotations}
                  activeHighlight={activeHighlight}
                  onHunkClick={handleHunkClick}
                  hunkRefs={hunkRefs}
                />
              ) : viewMode === 'clean' ? (
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 min-w-0">
                  <div className="font-serif text-sm leading-relaxed text-gray-800 whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere' }}>
                    {renderHighlightedDocument()}
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 min-w-0">
                  <textarea
                    ref={textareaRef}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full max-w-full resize-none border-0 focus:outline-none focus:ring-0 text-sm text-gray-800 leading-relaxed font-serif p-0"
                    style={{ minHeight: '100%', overflowWrap: 'anywhere' }}
                  />
                </div>
              )}
            </div>

            {showAnnotationsPanel && (
              <AnnotationsPanel
                annotations={filteredAnnotations}
                allAnnotations={annotations}
                severityFilter={severityFilter}
                setSeverityFilter={setSeverityFilter}
                severityCounts={severityCounts}
                expandedAnnotation={expandedAnnotation}
                activeHighlight={activeHighlight}
                matchedIds={matchedIds}
                linkedCount={linkedCount}
                onClose={() => setShowAnnotations(false)}
                onAnnotationClick={handleAnnotationClick}
                annotationCardRefs={annotationCardRefs}
                gradient={gradient}
                isCommercial={isCommercial}
              />
            )}
          </div>

          {/* Mobile layout */}
          <div className="lg:hidden flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {mobileTab === 'diff' && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col min-w-0 shadow-sm" style={{ minHeight: '400px' }}>
                <DiffView
                  originalText={originalText}
                  rewrittenText={rewrittenDocument}
                  annotations={annotations}
                  activeHighlight={activeHighlight}
                  onHunkClick={handleHunkClick}
                  hunkRefs={hunkRefs}
                />
              </div>
            )}

            {mobileTab === 'original' && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Original Document</span>
                </div>
                <div className="p-4 sm:p-6">
                  <AgreementDisplay content={originalText} className="text-gray-700" selectable />
                </div>
              </div>
            )}

            {mobileTab === 'rewritten' && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Rewritten Document</span>
                </div>
                <div className="p-4 sm:p-6">
                  <AgreementDisplay content={rewrittenDocument} className="text-gray-700" selectable />
                </div>
              </div>
            )}

            {mobileTab === 'annotations' && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                {hasAnnotations ? (
                  <AnnotationsPanel
                    annotations={filteredAnnotations}
                    allAnnotations={annotations}
                    severityFilter={severityFilter}
                    setSeverityFilter={setSeverityFilter}
                    severityCounts={severityCounts}
                    expandedAnnotation={expandedAnnotation}
                    activeHighlight={activeHighlight}
                    matchedIds={matchedIds}
                    linkedCount={linkedCount}
                    onClose={() => setMobileTab('diff')}
                    onAnnotationClick={handleAnnotationClick}
                    annotationCardRefs={annotationCardRefs}
                    isMobile
                    gradient={gradient}
                    isCommercial={isCommercial}
                  />
                ) : (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No annotations available for this document.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {!showAnnotations && hasAnnotations && (
            <button
              onClick={() => setShowAnnotations(true)}
              className={`fixed bottom-6 right-6 px-5 py-3 bg-gradient-to-r ${gradient} text-white rounded-full text-sm font-semibold shadow-lg hover:opacity-90 transition-all flex items-center gap-2 z-40`}
            >
              <MessageSquare className="w-4 h-4" />
              Show Annotations ({annotations.length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProcessStepsBar({ gradient, isCommercial }: { gradient: string; isCommercial: boolean }) {
  const steps = isCommercial
    ? [
        { label: 'Document Analysis', icon: FileSearch },
        { label: 'Clause Comparison', icon: GitCompareArrows },
        { label: 'Negotiation Strategy', icon: Scale },
      ]
    : [
        { label: 'Compliance Analysis', icon: FileSearch },
        { label: 'Document Rewrite', icon: Clipboard },
        { label: 'Comparative Analysis', icon: GitCompareArrows },
      ];

  return (
    <div className="mb-4 flex-shrink-0">
      <div className="bg-white rounded-xl border border-gray-200 px-5 py-3 shadow-sm">
        <div className="flex items-center justify-center gap-4 sm:gap-8">
          {steps.map((step, idx) => (
            <React.Fragment key={step.label}>
              <div className="flex items-center gap-2.5">
                <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                  <CheckCircle className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 hidden sm:inline">{step.label}</span>
                <step.icon className="w-3.5 h-3.5 text-gray-400 sm:hidden" />
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-6 sm:w-10 h-0.5 rounded-full bg-gradient-to-r ${gradient} opacity-30`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function AnalysisSummaryBar({
  showAnalysis,
  setShowAnalysis,
  analysis,
  annotations,
  isTenancy,
  isCommercial,
  gradient,
  negotiationSummary,
  showNegotiationSummary,
  setShowNegotiationSummary,
}: {
  showAnalysis: boolean;
  setShowAnalysis: (v: boolean) => void;
  analysis: Record<string, unknown>;
  annotations: Annotation[];
  isTenancy: boolean;
  isCommercial: boolean;
  gradient: string;
  negotiationSummary?: string;
  showNegotiationSummary: boolean;
  setShowNegotiationSummary: (v: boolean) => void;
}) {
  const summaryText = analysis.summary as string;
  const showSummary = summaryText && summaryText.length > 0;
  const a = analysis;

  const mustNegotiateCount = annotations.filter(
    (ann) => ann.severity === 'critical' || ann.severity === 'high',
  ).length;

  return (
    <div className="mb-4 flex-shrink-0 space-y-2">
      <button
        onClick={() => setShowAnalysis(!showAnalysis)}
        className="w-full flex items-center justify-between bg-white rounded-xl border border-gray-200 px-5 py-3.5 hover:bg-gray-50 transition-colors shadow-sm"
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <Info className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-900">Analysis Summary</span>
          <span className="text-xs text-gray-400 hidden sm:inline">
            ({annotations.length} {isCommercial ? 'differences found' : 'changes identified'})
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showAnalysis ? 'rotate-180' : ''}`} />
      </button>
      {showAnalysis && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm animate-slide-down">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {isTenancy ? (
              <>
                <StatCard value={String((a.issueCount as number) || 0)} label="Issues Found" icon={AlertTriangle} iconColor="text-red-500" bgColor="bg-red-50" />
                <StatCard value={`${(a.complianceScore as number) || 0}%`} label="Original Compliance" icon={Scale} iconColor="text-gray-700" bgColor="bg-gray-50" />
                <StatCard
                  value={((a.overallRisk as string) || 'N/A').charAt(0).toUpperCase() + ((a.overallRisk as string) || 'N/A').slice(1)}
                  label="Risk Level"
                  colorClass={riskColor(a.overallRisk as string)}
                  icon={Info}
                  iconColor={riskColor(a.overallRisk as string)}
                  bgColor="bg-gray-50"
                />
                <StatCard value={String(annotations.length)} label="Changes Made" colorClass="text-gray-900" icon={CheckCircle} iconColor="text-gray-900" bgColor="bg-gray-50" />
              </>
            ) : (
              <>
                <StatCard value={(a.leaseType as string) || 'Unknown'} label="Lease Type" small icon={Building2} iconColor="text-gray-700" bgColor="bg-gray-50" />
                <StatCard
                  value={((a.overallRisk as string) || 'N/A').charAt(0).toUpperCase() + ((a.overallRisk as string) || 'N/A').slice(1)}
                  label="Risk Level"
                  colorClass={riskColor(a.overallRisk as string)}
                  icon={Info}
                  iconColor={riskColor(a.overallRisk as string)}
                  bgColor="bg-gray-50"
                />
                <StatCard value={String(annotations.length)} label="Differences Found" icon={GitCompareArrows} iconColor="text-gray-700" bgColor="bg-gray-50" />
                <StatCard value={String(mustNegotiateCount)} label="Must-Negotiate" colorClass="text-red-600" icon={AlertTriangle} iconColor="text-red-500" bgColor="bg-red-50" />
              </>
            )}
          </div>
          {showSummary ? (
            <p className="mt-4 text-sm text-gray-600 leading-relaxed">{summaryText}</p>
          ) : (
            <p className="mt-4 text-sm text-gray-400 italic">Summary not available for this document.</p>
          )}
        </div>
      )}

      {isCommercial && negotiationSummary && (
        <>
          <button
            onClick={() => setShowNegotiationSummary(!showNegotiationSummary)}
            className="w-full flex items-center justify-between bg-white rounded-xl border border-gray-200 px-5 py-3.5 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
                <Scale className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-900">Negotiation Strategy</span>
              <span className="text-xs text-gray-500 hidden sm:inline font-medium">
                AI Attorney Recommendations
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showNegotiationSummary ? 'rotate-180' : ''}`} />
          </button>
          {showNegotiationSummary && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm animate-slide-down">
              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                {negotiationSummary}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ value, label, colorClass, small, icon: Icon, iconColor, bgColor }: {
  value: string; label: string; colorClass?: string; small?: boolean;
  icon?: React.FC<{ className?: string }>; iconColor?: string; bgColor?: string;
}) {
  return (
    <div className={`${bgColor || 'bg-gray-50'} rounded-xl border border-gray-100 p-3.5`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${iconColor || 'text-gray-400'}`} />}
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className={`${small ? 'text-sm' : 'text-xl'} font-bold ${colorClass || 'text-gray-900'} truncate`}>{value}</p>
    </div>
  );
}

function riskColor(risk: string): string {
  if (risk === 'critical') return 'text-red-600';
  if (risk === 'high') return 'text-gray-900';
  if (risk === 'medium') return 'text-gray-700';
  return 'text-gray-500';
}

interface AnnotationsPanelProps {
  annotations: Annotation[];
  allAnnotations: Annotation[];
  severityFilter: string;
  setSeverityFilter: (f: string) => void;
  severityCounts: Record<string, number>;
  expandedAnnotation: string | null;
  activeHighlight: string | null;
  matchedIds: Set<string>;
  linkedCount: number;
  onClose: () => void;
  onAnnotationClick: (id: string) => void;
  annotationCardRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  isMobile?: boolean;
  gradient?: string;
  isCommercial?: boolean;
}

function AnnotationsPanel({
  annotations,
  severityFilter,
  setSeverityFilter,
  severityCounts,
  expandedAnnotation,
  activeHighlight,
  matchedIds,
  linkedCount,
  onClose,
  onAnnotationClick,
  annotationCardRefs,
  isMobile,
  gradient,
  isCommercial,
}: AnnotationsPanelProps) {
  const wrapperClass = isMobile
    ? ''
    : 'rounded-r-xl border border-l-0 border-gray-200 flex flex-col min-h-0 min-w-0 shadow-sm';

  return (
    <div className={`bg-white overflow-hidden ${wrapperClass}`}>
      <div className="px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${gradient || 'from-gray-600 to-gray-800'} flex items-center justify-center`}>
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Annotations
              </h3>
              <p className="text-[10px] text-gray-400">
                {severityCounts.all} total
                {linkedCount > 0 && (
                  <span className="ml-1.5 text-gray-600 font-medium">{linkedCount} linked</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(['all', 'critical', 'high', 'medium', 'low'] as const).map((sev) => {
            const isActive = severityFilter === sev;
            const config = sev !== 'all' ? SEVERITY_CONFIG[sev] : null;
            return (
              <button
                key={sev}
                onClick={() => setSeverityFilter(sev)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isActive
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {config && !isActive && (
                  <div className={`w-2 h-2 rounded-full ${config.dot}`} />
                )}
                {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
                <span className={isActive ? 'text-white/70' : 'text-gray-400'}>{severityCounts[sev] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {annotations.length === 0 && (
          <div className="p-8 text-center">
            <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No annotations match this filter.</p>
          </div>
        )}
        {annotations.map((ann) => {
          const config = SEVERITY_CONFIG[ann.severity] || SEVERITY_CONFIG.low;
          const isExpanded = expandedAnnotation === ann.id;
          const isActive = activeHighlight === ann.id;
          const isMatched = matchedIds.has(ann.id);

          return (
            <div
              key={ann.id}
              ref={(el) => { annotationCardRefs.current[ann.id] = el; }}
              className={`border-b border-gray-100 last:border-b-0 transition-all duration-300 ${
                isActive ? 'bg-gray-50' : ''
              }`}
            >
              <button
                onClick={() => onAnnotationClick(ann.id)}
                className="w-full text-left px-4 py-3.5 hover:bg-gray-50/80 transition-colors"
              >
                <div className="flex items-start gap-2.5">
                  <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${config.dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${config.bg} ${config.color}`}>
                        {ann.category}
                      </span>
                      {isMatched && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 font-medium">
                          <ExternalLink className="w-2.5 h-2.5" />
                          linked
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{ann.reason}</p>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-2 animate-slide-down">
                  {ann.originalText && (
                    <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                      <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1.5">
                        {isCommercial ? 'Original Lease (Document A)' : 'Original'}
                      </p>
                      <p className="text-xs text-red-600 leading-relaxed">{ann.originalText}</p>
                    </div>
                  )}
                  {ann.newText && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                        {isCommercial ? 'Revised Lease (Document B)' : 'Replacement'}
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed">{ann.newText}</p>
                    </div>
                  )}
                  {isCommercial && (ann as Record<string, unknown>).recommendation && (
                    <div className={`rounded-lg p-3 border ${
                      (ann as Record<string, unknown>).recommendation === 'accept'
                        ? 'bg-gray-50 border-gray-300'
                        : (ann as Record<string, unknown>).recommendation === 'reject'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                    }`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${
                        (ann as Record<string, unknown>).recommendation === 'accept'
                          ? 'text-gray-900'
                          : (ann as Record<string, unknown>).recommendation === 'reject'
                            ? 'text-red-700'
                            : 'text-gray-700'
                      }`}>
                        Recommendation: {String((ann as Record<string, unknown>).recommendation).toUpperCase()}
                      </p>
                      {(ann as Record<string, unknown>).counterProposal && (
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {String((ann as Record<string, unknown>).counterProposal)}
                        </p>
                      )}
                    </div>
                  )}
                  {ann.legalReference && (
                    <div className="flex items-start gap-2 pt-1 px-1">
                      <Scale className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-500 leading-relaxed">{ann.legalReference}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
        <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
          {Object.entries(SEVERITY_CONFIG).map(([sev, conf]) => (
            <div key={sev} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${conf.dot}`} />
              <span className="capitalize">{sev}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
